import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  SEARCH_MIN_LENGTH,
  SEARCH_TYPE_LABELS,
  searchScore,
  type SearchHit,
  type SearchResults,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { NAV_SECTIONS } from './nav';

/**
 * One box for the whole platform.
 *
 * Two kinds of answer, deliberately in one list. **Records** come from the
 * server, which decides what this person may see — the client never filters
 * for access, because a client-side filter is a filter an operator can watch
 * arrive in the network tab. **Destinations** are pages, resolved here from
 * the nav the sidebar already builds: they need no query, they are already
 * capability-filtered, and "billing" should take you to Billing without a
 * round trip.
 *
 * The server's order is kept as it arrives. It scored the matches and sorted
 * them; re-sorting here with a slightly different idea of "better" is how the
 * top hit stops being the one somebody typed the serial of.
 */
export function UniversalSearch() {
  const navigate = useNavigate();
  const { can, session } = useSession();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const query = term.trim();

  /** Every page this person may open, as searchable rows. */
  const destinations = useMemo(() => {
    const isSuperadmin = session?.user.role === 'superadmin';
    return NAV_SECTIONS.flatMap((section) =>
      section.items
        .filter((item) => !item.external)
        .filter((item) => (item.superadminOnly ? isSuperadmin : true))
        .filter((item) => (item.capability ? can(item.capability) : true))
        .map((item) => ({ label: item.label, to: item.to, section: section.label })),
    );
  }, [can, session?.user.role]);

  const destinationHits: SearchHit[] = useMemo(() => {
    if (query.length < SEARCH_MIN_LENGTH) return [];
    return destinations
      .map((d) => ({ d, score: searchScore(d.label, query) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score || a.d.label.localeCompare(b.d.label))
      .slice(0, 5)
      .map(({ d, score }) => ({
        type: 'destination' as const,
        id: d.to,
        title: d.label,
        subtitle: d.section,
        url: d.to,
        badge: null,
        score: score as 1 | 2 | 3,
      }));
  }, [destinations, query]);

  // ⌘K / Ctrl-K from anywhere, Escape to leave. Registered once, on the
  // window, because the point of the shortcut is that it works when focus is
  // somewhere else entirely.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        window.setTimeout(() => inputRef.current?.focus(), 0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /*
    Debounced, and every answer is checked against the term that is in the box
    NOW: a slow response for "gac" must not paint over the results for
    "gachie1" that came back while it was in flight.
  */
  useEffect(() => {
    if (!open || query.length < SEARCH_MIN_LENGTH) {
      setResults(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const found = await api.get<SearchResults>('/search', { q: query });
        if (!cancelled && found.query === query) setResults(found);
      } catch {
        if (!cancelled) setResults(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query]);

  /** Destinations first — they are free, exact and usually what a short query means. */
  const groups = useMemo(() => {
    const record = results?.groups ?? [];
    return destinationHits.length > 0
      ? [
          { type: 'destination' as const, label: SEARCH_TYPE_LABELS.destination, hits: destinationHits, more: false },
          ...record,
        ]
      : record;
  }, [destinationHits, results]);

  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups]);
  useEffect(() => setCursor(0), [query]);

  const go = (hit: SearchHit) => {
    setOpen(false);
    setTerm('');
    setResults(null);
    navigate(hit.url);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter' && flat[cursor]) {
      e.preventDefault();
      go(flat[cursor]);
    }
  };

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
          window.setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="inline-flex items-center gap-2 rounded-lg border border-line bg-surface-2 px-3 py-1.5 text-xs text-muted hover:bg-surface-3"
        title="Search everything (Ctrl+K)"
      >
        <Search className="size-3.5" />
        <span className="hidden sm:inline">Search…</span>
        <kbd className="hidden rounded border border-line px-1 text-[10px] text-faint sm:inline">
          ⌘K
        </kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-xl overflow-hidden rounded-xl border border-line bg-surface shadow-2xl">
            <div className="flex items-center gap-2 border-b border-line px-4 py-3">
              <Search className="size-4 text-faint" />
              <input
                ref={inputRef}
                value={term}
                onChange={(e) => setTerm(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search ONUs, chassis, VLANs, people, pages…"
                className="w-full bg-transparent text-sm text-ink placeholder:text-faint focus:outline-none"
              />
              {loading && <span className="text-xs text-faint">…</span>}
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {query.length < SEARCH_MIN_LENGTH ? (
                <p className="px-4 py-6 text-center text-xs text-faint">
                  Type at least {SEARCH_MIN_LENGTH} characters. Only what you have access to is
                  searched.
                </p>
              ) : flat.length === 0 ? (
                <p className="px-4 py-6 text-center text-xs text-faint">
                  {loading ? 'Searching…' : `Nothing you can see matches “${query}”.`}
                </p>
              ) : (
                groups.map((group) => (
                  <div key={group.type}>
                    <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-faint">
                      {group.label}
                      {group.more && ' — showing the closest'}
                    </p>
                    {group.hits.map((h) => {
                      const index = flat.indexOf(h);
                      return (
                        <button
                          key={`${h.type}:${h.id}`}
                          onMouseEnter={() => setCursor(index)}
                          onClick={() => go(h)}
                          className={`flex w-full items-center justify-between gap-3 px-4 py-2 text-left ${
                            index === cursor ? 'bg-primary-soft' : 'hover:bg-surface-2'
                          }`}
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-ink">{h.title}</span>
                            {h.subtitle && (
                              <span className="block truncate text-xs text-muted">{h.subtitle}</span>
                            )}
                          </span>
                          {h.badge && (
                            <span className="shrink-0 text-[11px] text-faint">{h.badge}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between border-t border-line px-4 py-2 text-[11px] text-faint">
              <span>↑↓ to move · ↵ to open · esc to close</span>
              <span>Scoped to your access</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
